/**
 * ProjectLinksSection — Feb 2026 R293n
 *
 * CRUD surface for a project's quick links. Mounted inside the
 * Project > Overview tab under its own "Project links" section.
 * Non-admins see read-only chips they can click to open.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link as LinkIcon, Plus, Trash2, ExternalLink, Edit3, X, Check, GripVertical } from "lucide-react";
import ProjectLinkIcons, { kindMeta, detectKind } from "@/components/ProjectLinkIcons";

export default function ProjectLinksSection({ projectId, canEdit, onChanged }) {
  const [links, setLinks] = useState([]);
  // Feb 2026 R293o · HTML5 drag-and-drop reorder state. Admins pick up
  // a row by its grip handle; `dragIndex` is the index of the row being
  // dragged, `overIndex` is the row we'd drop above (visualised with a
  // 2px top border so the drop target is obvious).
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editUrl, setEditUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/links`);
      setLinks(data || []);
    } catch { setLinks([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId]);

  const add = async () => {
    if (!newLabel.trim() || !newUrl.trim()) {
      toast.error("Label and URL are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/links`, {
        label: newLabel.trim(),
        url: newUrl.trim(),
      });
      setLinks((cur) => [...cur, data]);
      setNewLabel(""); setNewUrl(""); setAdding(false);
      toast.success("Link added");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add link"));
    } finally { setBusy(false); }
  };

  const save = async (lid) => {
    if (!editLabel.trim() || !editUrl.trim()) {
      toast.error("Label and URL are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${projectId}/links/${lid}`, {
        label: editLabel.trim(),
        url: editUrl.trim(),
      });
      setLinks((cur) => cur.map((l) => (l.id === lid ? data : l)));
      setEditingId("");
      toast.success("Link updated");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't update link"));
    } finally { setBusy(false); }
  };

  const remove = async (lid) => {
    if (!window.confirm("Remove this link?")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/links/${lid}`);
      setLinks((cur) => cur.filter((l) => l.id !== lid));
      toast.success("Link removed");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't remove link"));
    } finally { setBusy(false); }
  };

  // Feb 2026 R293o · Commit a drag-and-drop reorder. Optimistic update
  // first, then persist to the backend. On failure we fall back to the
  // most recent server list so the UI never shows a stale order.
  const commitReorder = async (from, to) => {
    if (from === to || from == null || to == null) return;
    const reordered = links.slice();
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const prev = links;
    setLinks(reordered);
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/links/reorder`,
        { link_ids: reordered.map((l) => l.id) },
      );
      setLinks(data.project_links || reordered);
      toast.success("Order saved");
      onChanged?.();
    } catch (e) {
      setLinks(prev);
      toast.error(apiErrorMessage(e, "Couldn't save order"));
    }
  };

  if (loading) {
    return (
      <div className="border border-border bg-card/60 p-4 text-xs text-muted-foreground"
             data-testid="project-links-loading">
        Loading links…
      </div>
    );
  }

  return (
    <div className="border border-border bg-card/60 p-4 space-y-3" data-testid="project-links-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <LinkIcon className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Project links</h3>
          {links.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">· {links.length}</span>
          )}
        </div>
        {canEdit && !adding && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-7 text-xs"
            onClick={() => setAdding(true)}
            data-testid="project-links-add-btn">
            <Plus className="w-3 h-3 mr-1" /> Add link
          </Button>
        )}
      </div>

      {adding && canEdit && (
        <div className="border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2"
              data-testid="project-links-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              placeholder="Label (e.g. WhatsApp group)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="h-9 rounded-none sm:col-span-1"
              data-testid="project-links-new-label"
              maxLength={120}
            />
            <Input
              placeholder="https://…"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="h-9 rounded-none sm:col-span-2 font-mono text-xs"
              data-testid="project-links-new-url"
            />
          </div>
          {newUrl && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              Detected as:{" "}
              <span className="font-medium text-foreground">
                {kindMeta(detectKind(newUrl)).name}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs"
                     onClick={() => { setAdding(false); setNewLabel(""); setNewUrl(""); }}
                     data-testid="project-links-add-cancel">
              Cancel
            </Button>
            <Button size="sm" className="rounded-none h-7 text-xs" onClick={add} disabled={busy}
                     data-testid="project-links-add-save">
              <Check className="w-3 h-3 mr-1" /> Save link
            </Button>
          </div>
        </div>
      )}

      {links.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">
          No links yet. Add WhatsApp groups, Google Drive folders, Figma files or any URL your team opens often — they&apos;ll surface as service icons on the project header and the Projects list.
        </p>
      )}

      {links.length > 0 && (
        <ul className="space-y-1.5" data-testid="project-links-list">
          {links.map((link, idx) => {
            const meta = kindMeta(link.kind || detectKind(link.url));
            const Icon = meta.Icon;
            const isEditing = editingId === link.id;
            const isDropTarget = overIndex === idx && dragIndex !== null && dragIndex !== idx;
            return (
              <li key={link.id}
                   draggable={canEdit && !isEditing}
                   onDragStart={(e) => {
                     setDragIndex(idx);
                     e.dataTransfer.effectAllowed = "move";
                     // Firefox requires a data payload for drag to fire.
                     try { e.dataTransfer.setData("text/plain", link.id); } catch { /* noop */ }
                   }}
                   onDragOver={(e) => {
                     if (dragIndex === null) return;
                     e.preventDefault();
                     e.dataTransfer.dropEffect = "move";
                     if (overIndex !== idx) setOverIndex(idx);
                   }}
                   onDragLeave={() => {
                     if (overIndex === idx) setOverIndex(null);
                   }}
                   onDrop={(e) => {
                     e.preventDefault();
                     const from = dragIndex;
                     setDragIndex(null);
                     setOverIndex(null);
                     if (from !== null && from !== idx) commitReorder(from, idx);
                   }}
                   onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                   className={`flex items-center gap-2 border bg-background px-2 py-1.5 transition-[border-color] ${isDropTarget ? "border-primary border-t-2" : "border-border"} ${dragIndex === idx ? "opacity-50" : ""}`}
                   data-testid={`project-link-row-${link.id}`}>
                {canEdit && (
                  <span
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
                    title="Drag to reorder"
                    data-testid={`project-link-drag-${link.id}`}
                  >
                    <GripVertical className="w-3.5 h-3.5" />
                  </span>
                )}
                <span className={`inline-flex items-center justify-center h-6 w-6 border border-border bg-muted/30 shrink-0 ${meta.cls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                {isEditing ? (
                  <>
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      className="h-8 rounded-none text-xs flex-1 min-w-0"
                      maxLength={120}
                      data-testid={`project-link-edit-label-${link.id}`}
                    />
                    <Input
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      className="h-8 rounded-none text-xs flex-[2] min-w-0 font-mono"
                      data-testid={`project-link-edit-url-${link.id}`}
                    />
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                             onClick={() => setEditingId("")}
                             data-testid={`project-link-edit-cancel-${link.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                    <Button size="sm" className="rounded-none h-7 px-2" disabled={busy}
                             onClick={() => save(link.id)}
                             data-testid={`project-link-edit-save-${link.id}`}>
                      <Check className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" data-testid={`project-link-label-${link.id}`}>
                        {link.label}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate font-mono">{link.url}</div>
                    </div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs text-primary hover:underline"
                      title={`Open ${meta.name} link`}
                      data-testid={`project-link-open-${link.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(link.id);
                            setEditLabel(link.label);
                            setEditUrl(link.url);
                          }}
                          className="text-muted-foreground hover:text-foreground"
                          title="Edit"
                          data-testid={`project-link-edit-${link.id}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(link.id)}
                          className="text-muted-foreground hover:text-red-600"
                          title="Delete"
                          data-testid={`project-link-delete-${link.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {links.length > 0 && (
        <div className="pt-2 border-t border-dashed border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
            Preview — how it looks on the header + Projects list
          </div>
          <ProjectLinkIcons links={links} max={links.length} size="md" testidBase="project-links-preview" />
        </div>
      )}
    </div>
  );
}
